import { useCallback, useEffect, useRef, useState } from 'react';
import { coursesApi } from '../services/coursesApi';
import { logger } from '../../../lib/logger';
import { applyQuizStreamEvent, INITIAL_QUIZ_STREAM } from './quizStreamReducer';

// The backend can burst the whole reply in a handful of frames, which makes
// the typewriter feel instant/fake. We buffer arrivals in a ref and drain
// them onto screen at a capped, readable rate instead of rendering whatever
// arrived this tick. Tune these two after visual QA.
const DRAIN_TICK_MS = 16; // ~60fps
// Ease-out reveal: bigger jumps while far behind (so a big burst still
// catches up quickly), smaller jumps as we approach the tail (settles into
// a readable, deliberate typewriter pace). Adapts automatically to reply
// length instead of needing a fixed chars/tick constant.
const DRAIN_EASE = 0.15;

/**
 * Quiz hook - manages quiz state and interactions
 *
 * Supports two initialization modes:
 * 1. Legacy: Call initializeQuiz() which fetches from /quizzes/start
 * 2. Unified: Pass initialData from useSectionView (pre-loaded from /sections/view)
 *
 * Flow:
 * 1. Initial state comes from either initialData or /start endpoint
 * 2. On submit: POST /message → returns assistant response, score, feedback
 *
 * The backend is the source of truth. No local storage, no /session or /history calls.
 *
 * @param {{
 *   courseId: string,
 *   lessonId: string,
 *   initialData?: import('../types/sectionView.types').QuizContent | null,
 *   onCourseComplete?: () => void,
 *   onQuizPass?: () => void
 * }} params
 */
export function useQuiz({ courseId, lessonId, initialData = null, onCourseComplete, onQuizPass }) {
  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Session data
  const [sessionId, setSessionId] = useState(null);
  const [question, setQuestion] = useState('');
  const [difficulty, setDifficulty] = useState('');

  // Conversation
  const [messages, setMessages] = useState([]);

  // Completion state
  const [isPassed, setIsPassed] = useState(false);
  const [score, setScore] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [courseJustCompleted, setCourseJustCompleted] = useState(false);

  // User input
  const [inputValue, setInputValue] = useState('');

  // Streaming state (grading via SSE). `streamingReply` is the *displayed*
  // (throttled) text — see the drain loop in submitAnswer. The full,
  // as-arrived text lives in `fullTextRef` and is never rendered directly.
  const [streamingReply, setStreamingReply] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);

  // Refs
  const chatEndRef = useRef(null);
  const initRef = useRef(false);
  const hydratedSessionIdRef = useRef(null);
  const abortRef = useRef(null);

  // Drain-loop bookkeeping (see submitAnswer). `fullTextRef` accumulates
  // arrival text as it streams in; `displayedRef` mirrors `streamingReply`
  // state so the interval tick can read/advance it without waiting on a
  // React re-render. `pendingCommitRef` holds the authoritative `complete`
  // payload once grading is done, but it isn't applied to `messages`/score
  // until the display has caught up to the full text. `drainIntervalRef`
  // is the interval id; `drainDoneResolveRef` lets submitAnswer `await`
  // until the drain (and any deferred commit) has finished.
  const fullTextRef = useRef('');
  const displayedRef = useRef('');
  const pendingCommitRef = useRef(null);
  const drainIntervalRef = useRef(null);
  const drainDoneResolveRef = useRef(null);

  // Abort any in-flight grading stream / drain interval on unmount.
  useEffect(() => () => {
    try { abortRef.current?.abort(); } catch { /* noop */ }
    if (drainIntervalRef.current) {
      clearInterval(drainIntervalRef.current);
      drainIntervalRef.current = null;
    }
  }, []);

  // Hydrate state from initialData when it changes (unified endpoint mode)
  useEffect(() => {
    if (!initialData) return;

    // Only hydrate if this is a new session (different sessionId)
    if (hydratedSessionIdRef.current === initialData.sessionId) {
      return;
    }

    logger.log('[useQuiz] Hydrating from initialData, sessionId:', initialData.sessionId);

    hydratedSessionIdRef.current = initialData.sessionId;
    initRef.current = true; // Mark as initialized to prevent /start call

    setSessionId(initialData.sessionId);
    setQuestion(initialData.question || '');
    setDifficulty(initialData.difficulty || '');
    setMessages(initialData.messageHistory || []);
    setIsPassed(initialData.isPassed || false);
    setScore(initialData.score ?? null);
    setFeedback(null); // Feedback comes from /message responses
    setInputValue('');
    setError(null);
    setIsLoading(false);
  }, [initialData]);

  // Reset state when lessonId changes (legacy mode without initialData)
  useEffect(() => {
    // Skip reset if we're using unified mode with initialData
    // The initialData effect will handle the state update
    if (initialData) {
      return;
    }

    // Reset all state when lesson changes (legacy mode)
    logger.log('[useQuiz] Resetting state for lessonId change (legacy mode)');
    setSessionId(null);
    setQuestion('');
    setDifficulty('');
    setMessages([]);
    setIsPassed(false);
    setScore(null);
    setFeedback(null);
    setCourseJustCompleted(false);
    setInputValue('');
    setError(null);
    initRef.current = false;
    hydratedSessionIdRef.current = null;
  }, [lessonId, initialData]);

  // Initialize quiz session (legacy mode - calls /quizzes/start)
  const initializeQuiz = useCallback(async () => {
    if (!courseId || !lessonId) return;

    // Skip if already initialized (either from initialData or previous /start call)
    if (initRef.current) {
      logger.log('[useQuiz] Already initialized, skipping /start call');
      return;
    }
    initRef.current = true;

    setIsLoading(true);
    setError(null);

    try {
      logger.log('[useQuiz] Initializing quiz via /start for lesson:', lessonId);

      const data = await coursesApi.quizStart({
        courseId,
        lessonId,
        auth: true,
      });

      logger.log('[useQuiz] Start response:', data);

      setSessionId(data.sessionId);
      setQuestion(data.quiz?.question || '');
      setDifficulty(data.quiz?.difficulty || '');
      setMessages(data.messages || []);
      setIsPassed(data.isPassed || false);
      setScore(data.score ?? null);
      setFeedback(data.feedback ?? null);

    } catch (err) {
      console.error('[useQuiz] Failed to initialize:', err);
      setError('Failed to load quiz. Please try again.');
      initRef.current = false; // Allow retry
    } finally {
      setIsLoading(false);
    }
  }, [courseId, lessonId]);

  // Stop the drain interval and reset its bookkeeping. Safe to call whether
  // or not a drain is currently running.
  const stopDrain = useCallback(() => {
    if (drainIntervalRef.current) {
      clearInterval(drainIntervalRef.current);
      drainIntervalRef.current = null;
    }
  }, []);

  // Apply a `complete` payload to real state: append the assistant message,
  // set score/feedback/pass, fire pass/course-complete callbacks, and clear
  // the streaming buffers. Only called once the drain has fully caught up
  // to the authoritative text (see drainTick below) — so the reply always
  // finishes typing out before score/feedback/confetti appear.
  const finalizeCommit = useCallback(() => {
    const c = pendingCommitRef.current;
    pendingCommitRef.current = null;
    stopDrain();

    if (c) {
      logger.log('[useQuiz] Grading complete:', c);
      setMessages((prev) => [...prev, { role: 'assistant', content: c.assistantMessage.content, timestamp: c.assistantMessage.timestamp }]);
      setScore(c.score);
      setFeedback(c.feedback);
      if (c.status === 'PASSED') { setIsPassed(true); onQuizPass?.(); }
      if (c.courseJustCompleted) { setCourseJustCompleted(true); onCourseComplete?.(); }
    }

    fullTextRef.current = '';
    displayedRef.current = '';
    setStreamingReply('');
    setIsStreaming(false);
    setIsSubmitting(false);

    const resolveDrainDone = drainDoneResolveRef.current;
    drainDoneResolveRef.current = null;
    resolveDrainDone?.();
    // onQuizPass/onCourseComplete are parent callbacks; including them would
    // re-create this callback every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopDrain]);

  // One drain tick: advance the displayed text toward the full (as-arrived
  // or authoritative) text by an ease-out step, then — once fully caught up
  // and a commit is pending — finalize it.
  const drainTick = useCallback(() => {
    const full = fullTextRef.current;
    const current = displayedRef.current;
    if (current.length < full.length) {
      const remaining = full.length - current.length;
      const step = Math.max(1, Math.ceil(remaining * DRAIN_EASE));
      const next = full.slice(0, current.length + step);
      displayedRef.current = next;
      setStreamingReply(next);
    }
    if (pendingCommitRef.current && displayedRef.current.length >= fullTextRef.current.length) {
      finalizeCommit();
    }
  }, [finalizeCommit]);

  const startDrain = useCallback(() => {
    if (drainIntervalRef.current) return;
    drainIntervalRef.current = setInterval(drainTick, DRAIN_TICK_MS);
  }, [drainTick]);

  // Submit an answer — streams the grade via SSE (see quizStreamReducer /
  // docs/sse-quiz-grading-api-contract.md), with a single silent auto-retry
  // on a transport failure/stall (server-side dedup makes the retry safe).
  //
  // The reveal owns the timeline: tokens accumulate into `fullTextRef` as
  // they arrive, and a drain interval (started below) reveals them onto
  // `streamingReply` at a capped rate. On `complete`, we don't commit the
  // message/score immediately — we stash the payload in `pendingCommitRef`
  // and await the drain finishing, so the reply always finishes "typing"
  // before score/feedback/pass/confetti appear.
  const submitAnswer = useCallback(async () => {
    // Guards
    if (!sessionId) return;
    if (!inputValue.trim()) return;
    if (isSubmitting) return;
    if (isPassed) return;

    const userMessage = inputValue.trim();
    setIsSubmitting(true);
    setInputValue('');
    setError(null);

    // Optimistically add user message to UI
    const userMessageObj = {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMessageObj]);

    const removeOptimistic = () => setMessages((prev) => (prev.at(-1)?.role === 'user' && prev.at(-1)?.content === userMessage ? prev.slice(0, -1) : prev));

    // Clear any streaming buffers/interval left over from a prior attempt
    // and reset error/abort/stall paths to a clean, non-streaming state.
    const resetStreamState = () => {
      stopDrain();
      pendingCommitRef.current = null;
      fullTextRef.current = '';
      displayedRef.current = '';
      setStreamingReply('');
      setIsStreaming(false);
    };

    const runOnce = async () => {
      abortRef.current = new AbortController();
      fullTextRef.current = '';
      displayedRef.current = '';
      pendingCommitRef.current = null;
      setStreamingReply('');
      setIsStreaming(true);
      startDrain();

      let acc = INITIAL_QUIZ_STREAM;
      for await (const evt of coursesApi.quizMessageStream({ sessionId, message: userMessage, signal: abortRef.current.signal })) {
        acc = applyQuizStreamEvent(acc, evt);
        if (evt.event === 'token') fullTextRef.current = acc.streamingReply;
        if (acc.errored) break;
      }
      return acc;
    };

    let attempt = 0;
    // one silent auto-retry on a transport failure; dedup recovers the grade.
    // eslint-disable-next-line no-constant-condition
    while (true) {
      try {
        const acc = await runOnce();

        if (acc.errored) {                       // in-band EVALUATION_FAILED — no retry
          resetStreamState();
          removeOptimistic();
          setError(acc.errored.message);
          break;
        }
        if (acc.committed) {
          const c = acc.committed;
          // The committed message is authoritative — supersede whatever
          // token text arrived (may be partial/reordered) and let the
          // drain interval reveal the rest before we finalize.
          fullTextRef.current = c.assistantMessage.content;
          pendingCommitRef.current = c;
          startDrain();
          // eslint-disable-next-line no-loop-func
          await new Promise((resolve) => { drainDoneResolveRef.current = resolve; });
        } else {
          // Defensive: stream ended with neither a commit nor an in-band
          // error — nothing to reveal or finalize.
          resetStreamState();
        }
        break;
      } catch (err) {
        resetStreamState();

        if (err?.name === 'AbortError') { break; }              // user navigated away — nothing to recover
        if (err?.status) {                                       // pre-stream HTTP guard — no retry
          removeOptimistic();
          setError(err.code === 400 || err.status === 400 ? 'Your answer is empty or too long.' : (err.status === 403 || err.status === 404 ? 'This quiz session isn’t available.' : 'Failed to submit answer. Please try again.'));
          break;
        }
        // transport failure / stall
        console.error('[useQuiz] Stream failure:', err);
        if (attempt < 1) { attempt += 1; continue; }
        removeOptimistic();
        setError('Connection lost while grading. Your answer may have been recorded — tap Retry.');
        break;
      }
    }

    setIsSubmitting(false);
    // onQuizPass/onCourseComplete are parent callbacks; including them would
    // re-create this callback every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, inputValue, isSubmitting, isPassed, startDrain, stopDrain]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    try {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    } catch {
      // ignore
    }
  }, [messages, streamingReply]);

  // Retry function for error recovery
  const retry = useCallback(() => {
    initRef.current = false;
    hydratedSessionIdRef.current = null;
    initializeQuiz();
  }, [initializeQuiz]);

  return {
    // Loading states
    isLoading,
    isSubmitting,
    error,

    // Session data
    sessionId,
    question,
    difficulty,

    // Conversation
    messages,

    // Completion state
    isPassed,
    score,
    feedback,
    courseJustCompleted,

    // User input
    inputValue,
    setInputValue,

    // Streaming state
    streamingReply,
    isStreaming,

    // Actions
    initializeQuiz,
    submitAnswer,
    retry,

    // Refs
    chatEndRef,
  };
}
